/**
 * Form Request Validation
 * Handles client-side validation for the form request component
 */

( function() {
	'use strict';

	/**
	 * Initialize form validation
	 */
	function initFormValidation() {
		// Find actual form elements, not just wrapper divs
		const formContainers = document.querySelectorAll( '.form-request' );

		formContainers.forEach( function( container ) {
			// Skip Gravity Forms - they have their own validation system
			if ( container.querySelector( '.gform_wrapper' ) ) {
				return;
			}

			// For Pattern Lab forms, the container itself should be the form element
			// If it's not a form element, look for a form inside
			let form = container;
			if ( container.tagName !== 'FORM' ) {
				form = container.querySelector( 'form' );
				if ( !form ) {
					return; // No form found, skip
				}
			}

			// Disable HTML5 validation to use custom validation
			form.setAttribute( 'novalidate', 'novalidate' );

			// Handle form submission
			form.addEventListener( 'submit', function( e ) {
				if ( !validateForm( container ) ) {
					e.preventDefault();
					e.stopPropagation();
				}
			} );

			// Real-time validation on blur
			const inputs = form.querySelectorAll( 'input[required], textarea[required], select[required]' );
			inputs.forEach( function( input ) {
				input.addEventListener( 'blur', function() {
					validateField( input );
				} );

				// Clear error on input
				input.addEventListener( 'input', function() {
					if ( input.classList.contains( 'form-request__input--error' ) ) {
						clearFieldError( input );
					}
				} );
			} );

			// Checkbox group validation
			const checkboxGroups = form.querySelectorAll( '.form-request__checkbox-column' );
			checkboxGroups.forEach( function( group ) {
				const checkboxes = group.querySelectorAll( 'input[type="checkbox"]' );
				checkboxes.forEach( function( checkbox ) {
					checkbox.addEventListener( 'change', function() {
						validateCheckboxGroup( group );
					} );
				} );
			} );
		} );
	}

	/**
	 * Validate entire form
	 * @param {HTMLFormElement} form
	 * @returns {boolean}
	 */
	function validateForm( form ) {
		let isValid = true;

		// Clear all previous errors
		clearAllErrors( form );

		// Validate text inputs and textareas
		const inputs = form.querySelectorAll( 'input[required], textarea[required], select[required]' );
		inputs.forEach( function( input ) {
			if ( !validateField( input ) ) {
				isValid = false;
			}
		} );

		// Validate checkbox groups
		const checkboxGroups = form.querySelectorAll( '.form-request__checkbox-column' );
		checkboxGroups.forEach( function( group ) {
			if ( !validateCheckboxGroup( group ) ) {
				isValid = false;
			}
		} );

		// Focus on first error
		if ( !isValid ) {
			const firstError = form.querySelector( '.form-request__input--error, .form-request__checkbox-column--error' );
			if ( firstError ) {
				firstError.focus();
				// Scroll to error with offset for fixed headers
				const yOffset = -100;
				const y = firstError.getBoundingClientRect().top + window.pageYOffset + yOffset;
				window.scrollTo( { top: y, behavior: 'smooth' } );
			}
		}

		return isValid;
	}

	/**
	 * Validate a single field
	 * @param {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement} field
	 * @returns {boolean}
	 */
	function validateField( field ) {
		const value = field.value.trim();
		const fieldGroup = field.closest( '.form-request__group' );

		if ( !fieldGroup ) return true;

		// Check if field is required and empty
		if ( field.hasAttribute( 'required' ) && value === '' ) {
			showFieldError( field, 'Please fill out this field.' );
			return false;
		}

		// Email validation
		if ( field.type === 'email' && value !== '' ) {
			const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if ( !emailPattern.test( value ) ) {
				showFieldError( field, 'Please enter a valid email address.' );
				return false;
			}
		}

		// Phone validation (basic)
		if ( field.type === 'tel' && value !== '' ) {
			const phonePattern = /^[\d\s\-()+-]+$/;
			if ( !phonePattern.test( value ) || value.replace( /\D/g, '' ).length < 10 ) {
				showFieldError( field, 'Please enter a valid phone number.' );
				return false;
			}
		}

		clearFieldError( field );
		return true;
	}

	/**
	 * Validate checkbox group (at least one must be checked)
	 * @param {HTMLElement} group
	 * @returns {boolean}
	 */
	function validateCheckboxGroup( group ) {
		const checkboxes = group.querySelectorAll( 'input[type="checkbox"]' );
		const checkedCount = Array.from( checkboxes ).filter( cb => cb.checked ).length;
		const title = group.querySelector( '.form-request__column-title' );

		if ( title && title.querySelector( '.form-request__required' ) ) {
			if ( checkedCount === 0 ) {
				showCheckboxGroupError( group, 'Please select at least one option.' );
				return false;
			}
		}

		clearCheckboxGroupError( group );
		return true;
	}

	/**
	 * Show field error
	 * @param {HTMLElement} field
	 * @param {string} message
	 */
	function showFieldError( field, message ) {
		field.classList.add( 'form-request__input--error' );
		field.setAttribute( 'aria-invalid', 'true' );

		const fieldContainer = field.closest( '.form-request__field' );
		if ( fieldContainer ) {
			let errorElement = fieldContainer.querySelector( '.form-request__error' );
			if ( errorElement ) {
				errorElement.textContent = message;
				errorElement.style.display = 'block';
			}
		}
	}

	/**
	 * Clear field error
	 * @param {HTMLElement} field
	 */
	function clearFieldError( field ) {
		field.classList.remove( 'form-request__input--error' );
		field.removeAttribute( 'aria-invalid' );

		const fieldContainer = field.closest( '.form-request__field' );
		if ( fieldContainer ) {
			const errorElement = fieldContainer.querySelector( '.form-request__error' );
			if ( errorElement ) {
				errorElement.style.display = 'none';
			}
		}
	}

	/**
	 * Show checkbox group error
	 * @param {HTMLElement} group
	 * @param {string} message
	 */
	function showCheckboxGroupError( group, message ) {
		group.classList.add( 'form-request__checkbox-column--error' );

		let errorElement = group.querySelector( '.form-request__checkbox-error' );
		if ( !errorElement ) {
			errorElement = document.createElement( 'span' );
			errorElement.className = 'form-request__checkbox-error';
			const title = group.querySelector( '.form-request__column-title' );
			if ( title ) {
				title.insertAdjacentElement( 'afterend', errorElement );
			}
		}
		errorElement.textContent = message;
		errorElement.style.display = 'block';
	}

	/**
	 * Clear checkbox group error
	 * @param {HTMLElement} group
	 */
	function clearCheckboxGroupError( group ) {
		group.classList.remove( 'form-request__checkbox-column--error' );

		const errorElement = group.querySelector( '.form-request__checkbox-error' );
		if ( errorElement ) {
			errorElement.style.display = 'none';
		}
	}

	/**
	 * Clear all errors in form
	 * @param {HTMLFormElement} form
	 */
	function clearAllErrors( form ) {
		// Clear input errors
		const errorInputs = form.querySelectorAll( '.form-request__input--error' );
		errorInputs.forEach( function( input ) {
			clearFieldError( input );
		} );

		// Clear checkbox group errors
		const errorGroups = form.querySelectorAll( '.form-request__checkbox-column--error' );
		errorGroups.forEach( function( group ) {
			clearCheckboxGroupError( group );
		} );
	}

	// Initialize when DOM is ready
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', initFormValidation );
	} else {
		initFormValidation();
	}

	// Re-initialize for dynamically loaded forms
	window.addEventListener( 'chia:reinit-forms', initFormValidation );

} )();
